export type SpellActionKind = 'damage' | 'heal' | 'status';

export type SpellAction = {
  kind: SpellActionKind;
  amount: number;
  name?: string;
  effect?: string;
  trigger_kind?: string;
  trigger_amount?: number;
};

export type SpellClause = {
  targetCount: number | null;
  actions: SpellAction[];
  summary: string;
};

export const SPELL_EFFECT_EXAMPLES = [
  {
    formula: '1 Ziel: 6 Schaden, Eingefroren',
    label: 'Ein Ziel — Eisstrahl',
    hint: 'Ein Gegner erleidet 6 Schaden und den Status Eingefroren, bis der Admin ihn aufhebt.',
  },
  {
    formula: '3 Ziele: 4 Schaden',
    label: 'Mehrere Ziele',
    hint: 'Bis zu drei gewählte Ziele erleiden je 4 Schaden.',
  },
  {
    formula: '1 Gegner: 4 Schaden; 3 Ziele: -2 Bewegung',
    label: 'Kombination — Treffer und Umkreis',
    hint: 'Erstes Ziel: 4 Schaden. Die nächsten drei (im Umkreis): Verlangsamt (−2 Bewegung), bis der Admin den Effekt aufhebt.',
  },
  {
    formula: '1 Ziel: 8 Schaden, Brennen 3',
    label: 'Feuer mit Brennen',
    hint: 'Sofort 8 Schaden. Danach löst der Admin Brennen aus (je 3 Schaden), bis er den Effekt beendet.',
  },
] as const;

const NATURAL_COMBO = /(?:trifft\s+)?(?:einen|1)\s+gegner\s+für\s+(\d+)\s+schaden\s+und\s+verlangsam(?:t|en)\s+(\d+)\s+(?:spieler|ziele|gegner)(?:\s+im\s+umkreis)?\s+(?:für|um)\s+(\d+)\s+bewegung/i;
const TARGET_PREFIX = /^(?:(?:trifft|wirkt)\s+)?(?:(einen|eine|ein)\s+(?:gegner|ziel|spieler)|(\d+)\s+(?:ziele?|gegner|spieler)(?:\s+im\s+umkreis)?)\s*(?::|,|für)\s*/i;
const BURN_RE = /(\d+)\s*(?:brennen|brandschaden|brand)\b|\b(?:brennen|brandschaden|brand)\s*[:+]?\s*(\d+)/gi;
const SLOW_WORD_RE = /\bverlangsam(?:t|en|ung)?\s+(\d+)\b/gi;
const MOVE_SIGNED_RE = /([+-]\d+)\s*bewegung\b|\bbewegung\s*[:+]?\s*([+-]\d+)/gi;
const DAMAGE_RE = /(\d+)\s*schaden\b|\bschaden\s*[:+]?\s*(\d+)|\bfür\s+(\d+)\s+schaden\b/gi;
const HEAL_RE = /(\d+)\s*(?:heilung|heil|leben|hp|lp)\b|\b(?:heilung|heil|heilt|leben|hp|lp)\s*[:+]?\s*(\d+)/gi;
const NAMED_STATUS: Array<[string, string]> = [
  ['eingefroren', 'Eingefroren'],
  ['einfrier', 'Eingefroren'],
  ['frost', 'Eingefroren'],
  ['betäub', 'Betäubt'],
  ['betaeub', 'Betäubt'],
  ['stun', 'Betäubt'],
  ['unsichtbar', 'Unsichtbar'],
  ['vergiftet', 'Vergiftet'],
];

function wordCount(token?: string | null) {
  const value = (token || '').trim().toLowerCase();
  if (['einen', 'eine', 'ein'].includes(value)) return 1;
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

function clauseSummary(targetCount: number | null, actions: SpellAction[]) {
  const count = targetCount == null ? 1 : targetCount;
  const who = count === 1 ? '1 Ziel' : `${count} Ziele`;
  const bits = actions.map((action) => {
    if (action.kind === 'damage') return `${action.amount} Schaden`;
    if (action.kind === 'heal') return `Heilung ${action.amount}`;
    return action.effect || action.name || 'Effekt';
  });
  return bits.length ? `${who}: ${bits.join(', ')}` : who;
}

function parseActions(body: string, healingHint = false): SpellAction[] {
  const text = (body || '').trim();
  const blob = text.toLowerCase();
  const actions: SpellAction[] = [];
  const used: Array<[number, number]> = [];

  const collect = (regex: RegExp, pick: (match: RegExpExecArray) => void) => {
    regex.lastIndex = 0;
    let match = regex.exec(blob);
    while (match) {
      used.push([match.index, match.index + match[0].length]);
      pick(match);
      match = regex.exec(blob);
    }
  };

  collect(BURN_RE, (match) => {
    const amount = parseInt(match[1] || match[2] || '0', 10);
    if (amount <= 0) return;
    actions.push({
      kind: 'status',
      amount,
      name: 'Brennen',
      effect: `Brennen ${amount}`,
      trigger_kind: 'burn',
      trigger_amount: amount,
    });
  });

  let slow = 0;
  collect(SLOW_WORD_RE, (match) => {
    slow = Math.max(slow, parseInt(match[1] || '0', 10));
  });
  collect(MOVE_SIGNED_RE, (match) => {
    const value = parseInt(match[1] || match[2] || '0', 10);
    if (value < 0) slow = Math.max(slow, Math.abs(value));
  });
  if (slow) {
    actions.push({
      kind: 'status',
      amount: slow,
      name: 'Verlangsamt',
      effect: `-${slow} Bewegung`,
      trigger_kind: '',
      trigger_amount: 0,
    });
  }

  let heal = 0;
  collect(HEAL_RE, (match) => {
    heal += parseInt(match[1] || match[2] || '0', 10);
  });
  let damage = 0;
  collect(DAMAGE_RE, (match) => {
    damage += parseInt(match[1] || match[2] || match[3] || '0', 10);
  });

  let leftover = text;
  for (const [start, end] of [...used].sort((a, b) => b[0] - a[0])) {
    leftover = `${leftover.slice(0, start)} ${leftover.slice(end)}`;
  }
  let leftoverBlob = leftover.toLowerCase();
  let named: string | null = null;
  for (const [marker, label] of NAMED_STATUS) {
    if (leftoverBlob.includes(marker) || blob.includes(marker)) {
      named = label;
      leftoverBlob = leftoverBlob.replace(marker, ' ');
      break;
    }
  }
  const leftoverClean = leftoverBlob.replace(/[\s,.:;+\-]+/g, ' ').trim();

  if (named) {
    if (slow) {
      const existing = actions.find((action) => action.name === 'Verlangsamt');
      if (existing) {
        existing.name = named;
        existing.effect = `-${slow} Bewegung`;
      }
    } else {
      actions.push({
        kind: 'status',
        amount: 0,
        name: named,
        effect: named,
        trigger_kind: '',
        trigger_amount: 0,
      });
    }
  }

  if (heal && !damage) {
    actions.unshift({ kind: 'heal', amount: heal });
  } else if (damage) {
    actions.unshift({ kind: 'damage', amount: damage });
    if (heal) actions.splice(1, 0, { kind: 'heal', amount: heal });
  } else if (/^\d+$/.test(leftoverClean)) {
    actions.unshift({ kind: healingHint ? 'heal' : 'damage', amount: parseInt(leftoverClean, 10) });
  } else if (!actions.length) {
    const match = blob.match(/\d+/);
    if (match) {
      actions.push({ kind: healingHint ? 'heal' : 'damage', amount: parseInt(match[0], 10) });
    } else if (leftoverClean) {
      const label = leftover.trim() || text;
      actions.push({
        kind: 'status',
        amount: 0,
        name: label.slice(0, 80) || 'Effekt',
        effect: label,
        trigger_kind: '',
        trigger_amount: 0,
      });
    }
  }
  return actions;
}

function splitClauses(text: string): string[] {
  const combo = text.match(NATURAL_COMBO);
  if (combo) {
    return [`1 Gegner: ${combo[1]} Schaden`, `${combo[2]} Ziele: -${combo[3]} Bewegung`];
  }
  if (text.includes(';')) {
    return text.split(';').map((part) => part.trim()).filter(Boolean);
  }
  return text.trim() ? [text.trim()] : [];
}

export function parseSpellEffect(text: string, healingHint = false): SpellClause[] {
  const parts = splitClauses(text || '');
  const clauses: SpellClause[] = [];
  for (const part of parts) {
    const prefix = part.match(TARGET_PREFIX);
    const targetCount = prefix ? wordCount(prefix[1] || prefix[2]) : null;
    const body = prefix ? part.slice(prefix[0].length).trim() || part : part;
    const actions = parseActions(body, healingHint);
    if (!actions.length) continue;
    clauses.push({
      targetCount,
      actions,
      summary: clauseSummary(targetCount, actions),
    });
  }
  if (!clauses.length) {
    const actions = parseActions(text || '', healingHint);
    if (actions.length) {
      clauses.push({ targetCount: null, actions, summary: clauseSummary(null, actions) });
    }
  }
  return clauses;
}

export function spellNeededTargets(text: string | undefined, previewCount?: number): number {
  if (typeof previewCount === 'number' && previewCount > 0) return previewCount;
  const clauses = parseSpellEffect(text || '');
  let total = 0;
  let counted = false;
  for (const clause of clauses) {
    if (clause.targetCount == null) continue;
    counted = true;
    total += clause.targetCount;
  }
  return counted ? total : 1;
}

export function spellEffectSummary(text: string | undefined, fallback = ''): string {
  const clauses = parseSpellEffect(text || '');
  if (!clauses.length) return fallback;
  return clauses.map((clause) => clause.summary).join('; ');
}

const BURN_NAME_RE = /brennen|brandschaden|(?:^|[^a-zäöü])brand(?:$|[^a-zäöü])/i;
const BURN_AMOUNT_RE = /(\d+)\s*(?:brennen|brandschaden|brand)\b|\b(?:brennen|brandschaden|brand)\s*[:+]?\s*(\d+)/i;

export function burnTriggerAmount(effect?: { trigger_kind?: string | null; trigger_amount?: number; name?: string; effect?: string } | null) {
  if (!effect) return 0;
  if ((effect.trigger_kind || '').toLowerCase() === 'heal') return 0;
  const stored = Number(effect.trigger_amount || 0);
  if (Number.isFinite(stored) && stored > 0) return stored;
  const blob = `${effect.name || ''} ${effect.effect || ''}`;
  const match = blob.match(BURN_AMOUNT_RE);
  if (match) return Math.abs(parseInt(match[1] || match[2] || '0', 10)) || 0;
  if (BURN_NAME_RE.test(blob)) {
    const number = blob.match(/(\d+)/);
    return number ? Math.abs(parseInt(number[1], 10)) || 0 : 0;
  }
  return 0;
}

const HEAL_REPEAT_RE = /heilung\s+(\d+)\s*×\s*(\d+)/i;

export function isRepeatHeal(effect?: { trigger_kind?: string | null; trigger_amount?: number; trigger_charges?: number; effect?: string } | null) {
  if (!effect) return false;
  if ((effect.trigger_kind || '').toLowerCase() === 'heal') return true;
  return HEAL_REPEAT_RE.test(effect.effect || '');
}

export function repeatHealAmount(effect?: { trigger_kind?: string | null; trigger_amount?: number; effect?: string } | null) {
  if (!effect || !isRepeatHeal(effect)) return 0;
  const stored = Number(effect.trigger_amount || 0);
  if (Number.isFinite(stored) && stored > 0) return stored;
  const match = (effect.effect || '').match(HEAL_REPEAT_RE);
  return match ? parseInt(match[1], 10) || 0 : 0;
}

export function repeatHealCharges(effect?: { trigger_charges?: number; effect?: string; trigger_kind?: string | null } | null) {
  if (!effect || !isRepeatHeal(effect)) return 0;
  const stored = Number(effect.trigger_charges || 0);
  if (Number.isFinite(stored) && stored > 0) return stored;
  const match = (effect.effect || '').match(HEAL_REPEAT_RE);
  return match ? parseInt(match[2], 10) || 0 : 0;
}

export function isBurnEffect(effect?: { trigger_kind?: string | null; trigger_amount?: number; name?: string; effect?: string } | null) {
  if (!effect) return false;
  if ((effect.trigger_kind || '').toLowerCase() === 'heal') return false;
  if ((effect.trigger_kind || '').toLowerCase() === 'burn') return true;
  return BURN_NAME_RE.test(`${effect.name || ''} ${effect.effect || ''}`) || burnTriggerAmount(effect) > 0;
}
